import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { queryKeys } from '@/lib/query-keys'
import { api } from '@/lib/api'
import { motion, useScroll, useTransform, type MotionValue } from 'framer-motion';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Plane, Bell, ChevronDown, User, LogOut, Luggage, Shield } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import ThemeToggle from "@/components/ThemeToggle";
import MobileMenu from "@/components/layout/MobileMenu";
import NotificationFeed from "@/components/layout/NotificationFeed";
import { formatProfileBadgeLabel } from "@/lib/membershipTier";

const Logo = ({ isHome, scrollY, isDark }: { isHome: boolean; scrollY: MotionValue<number>; isDark: boolean }) => {
    const textColor = useTransform(scrollY, [100, 300], ["#0f172a", "#ffffff"]);
    const subColor = useTransform(scrollY, [100, 300], ["#00A3B5", "#00A3B5"]);

    return (
        <Link to="/" className="flex items-center gap-3">
            <div className="relative w-14 h-14 scale-100 group">
                {/* Plane-free badge: the asset has the static plane removed so only
                    the orbiting plane below animates around the temple. */}
                <img
                    src="/pantheon-mark-noplane.png"
                    alt="Pantheon Points"
                    width={56}
                    height={56}
                    className="w-14 h-14 rounded-full border border-pantheon-teal/40 object-cover shadow-xl shadow-pantheon-teal/20 transition-transform group-hover:scale-105"
                />
                <motion.div
                    className="absolute inset-0 z-20 pointer-events-none"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 7, repeat: Infinity, ease: "linear" }}
                >
                    <Plane
                        className="w-3.5 h-3.5 text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]"
                        style={{ position: "absolute", top: "2px", left: "50%", transform: "translateX(-50%) rotate(45deg)" }}
                    />
                </motion.div>
            </div>

            <div className="flex flex-col">
                <motion.span
                    style={{ color: isHome && !isDark ? textColor : "#ffffff" }}
                    className="font-display font-bold uppercase tracking-[0.2em] text-sm"
                >
                    Pantheon
                </motion.span>
                <motion.span
                    style={{ color: subColor }}
                    className="font-sans text-[8px] font-bold tracking-[0.3em] uppercase"
                >
                    Points
                </motion.span>
            </div>
        </Link>
    );
};

export default function Navbar() {
    const { isLoggedIn, user, logout } = useAuth();
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const isAdmin = ['admin', 'super_admin'].includes((user?.role ?? '').toLowerCase());
    const profileBadgeLabel = formatProfileBadgeLabel(user?.membershipTier, user?.role);
    const accountInitials = `${user?.firstName?.[0] ?? ''}${user?.lastName?.[0] ?? ''}`.toUpperCase() || 'U';
    const navigate = useNavigate();
    const [bellOpen, setBellOpen] = useState(false);

    const location = useLocation();
    const isHome = location.pathname === '/';
    const { scrollY } = useScroll();

    const bgOpacity = useTransform(scrollY, [200, 400], ["rgba(15, 23, 42, 0)", "rgba(15, 23, 42, 1)"]);
    const linkColor = useTransform(scrollY, [100, 300], ["#0f172a", "#ffffff"]);
    const shadowOpacity = useTransform(scrollY, [200, 400], ["rgba(0, 0, 0, 0)", "rgba(0, 0, 0, 0.1)"]);

    const { data: unreadData } = useQuery({
        queryKey: queryKeys.notificationUnreadCount,
        queryFn: () => api.getUnreadCount(),
        enabled: isLoggedIn,
        refetchInterval: 60_000,
    });

    const unreadCount = unreadData?.count ?? 0;
    const badgeLabel = unreadCount > 9 ? '9+' : String(unreadCount);

    const navLinks = [
        { name: 'HOME', path: '/' },
        { name: 'SEARCH', path: '/search' },
        { name: 'PRICING', path: '/pricing' },
        { name: 'ABOUT', path: '/about' },
        { name: 'BLOG', path: '/blog' },
        { name: 'FAQ', path: '/faqs' },
        { name: 'CONTACT US', path: '/contact' },
    ];

    return (
        <motion.nav
            style={{
                backgroundColor: isHome ? bgOpacity : "rgba(15, 23, 42, 1)",
                boxShadow: isHome ? `0 10px 15px -3px ${shadowOpacity.get()}` : "0 10px 15px -3px rgba(0, 0, 0, 0.1)"
            }}
            className="fixed top-0 left-0 right-0 z-[100] py-2 transition-colors duration-500"
        >
            <div className="max-w-[1700px] mx-auto px-6 h-16 flex items-center justify-between">
                <Logo isHome={isHome} scrollY={scrollY} isDark={isDark} />

                <div className="hidden lg:flex items-center gap-10">
                    {navLinks.map((link) => (
                        <Link
                            key={link.name}
                            to={link.path}
                        >
                            <motion.span
                                style={{ color: isHome && !isDark ? linkColor : "#ffffff" }}
                                className={`text-[11px] font-bold tracking-[0.2em] transition-all relative group ${location.pathname === link.path ? 'text-pantheon-teal' : ''
                                    }`}
                            >
                                {link.name}
                                <span className={`absolute -bottom-1 left-0 w-full h-0.5 bg-pantheon-teal transition-transform duration-300 origin-left ${location.pathname === link.path ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
                                    }`} />
                            </motion.span>
                        </Link>
                    ))}
                </div>

                <div className="hidden lg:flex items-center gap-4">
                    {isLoggedIn ? (
                        <div className="flex items-center gap-6 pl-6 border-l border-white/10">
                            <ThemeToggle className="text-white" />
                            <DropdownMenu open={bellOpen} onOpenChange={setBellOpen}>
                                <DropdownMenuTrigger asChild>
                                    <motion.button
                                        aria-label="Notifications"
                                        style={{ color: isHome && !isDark ? linkColor : "#ffffff" }}
                                        className="relative p-2 opacity-70 hover:opacity-100 transition-opacity"
                                    >
                                        <Bell className="w-5 h-5" />
                                        {unreadCount > 0 && (
                                            <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 bg-pantheon-teal rounded-full border-2 border-navy-900 flex items-center justify-center text-[8px] font-black text-white px-0.5">
                                                {badgeLabel}
                                            </span>
                                        )}
                                    </motion.button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-80 mt-2 bg-navy-900 border-white/10 text-white p-2 rounded-xl shadow-2xl backdrop-blur-xl border-none">
                                    <NotificationFeed
                                        isOpen={isLoggedIn && bellOpen}
                                        membershipTier={user?.membershipTier}
                                        onClose={() => setBellOpen(false)}
                                    />
                                </DropdownMenuContent>
                            </DropdownMenu>

                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <div className="flex items-center gap-3 group cursor-pointer relative">
                                        <div
                                            className="w-9 h-9 rounded-full border-2 border-pantheon-teal/30 p-0.5 group-hover:border-pantheon-teal transition-all overflow-hidden"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                window.location.href = '/dashboard';
                                            }}
                                        >
                                            <Avatar className="w-full h-full">
                                                {user?.avatar ? <AvatarImage src={user.avatar} alt="Profile" className="object-cover" /> : null}
                                                <AvatarFallback className="bg-pantheon-teal/20 text-white text-[11px] font-bold">{accountInitials}</AvatarFallback>
                                            </Avatar>
                                        </div>
                                        <div className="hidden xl:flex flex-col text-left">
                                            <motion.span
                                                style={{ color: isHome && !isDark ? linkColor : "#ffffff" }}
                                                className="text-[11px] font-bold tracking-wide"
                                            >
                                                {`${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() || 'User'}
                                            </motion.span>
                                            <span className="text-[9px] text-pantheon-teal font-medium tracking-wider uppercase">{profileBadgeLabel}</span>
                                        </div>
                                        <motion.div
                                            style={{ color: isHome && !isDark ? linkColor : "#ffffff" }}
                                            className="ml-1 opacity-40 group-hover:opacity-100 transition-opacity"
                                        >
                                            <ChevronDown className="w-4 h-4" />
                                        </motion.div>
                                    </div>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-56 mt-2 bg-navy-900 border-white/10 text-white p-2 rounded-xl shadow-2xl backdrop-blur-xl">
                                    <DropdownMenuLabel className="px-3 py-2 text-[10px] font-black uppercase tracking-[0.2em] text-white/40">My Account</DropdownMenuLabel>
                                    <DropdownMenuSeparator className="bg-white/5 mx-1" />
                                    {isAdmin && (
                                        <DropdownMenuItem asChild>
                                            <Link to="/admin" className="w-full flex items-center gap-3 px-3 py-2.5 text-[11px] font-bold tracking-wide hover:bg-white/5 rounded-lg transition-colors cursor-pointer group">
                                                <div className="w-8 h-8 rounded-full bg-[#003366] flex items-center justify-center text-white group-hover:bg-pantheon-teal transition-colors">
                                                    <Shield className="w-4 h-4" />
                                                </div>
                                                <span>Admin Panel</span>
                                            </Link>
                                        </DropdownMenuItem>
                                    )}
                                    <DropdownMenuItem asChild>
                                        <Link to="/dashboard" className="w-full flex items-center gap-3 px-3 py-2.5 text-[11px] font-bold tracking-wide hover:bg-white/5 rounded-lg transition-colors cursor-pointer group">
                                            <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-white group-hover:bg-pantheon-teal transition-colors">
                                                <Luggage className="w-4 h-4" />
                                            </div>
                                            <span>Dashboard</span>
                                        </Link>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem asChild>
                                        <Link to="/settings" className="w-full flex items-center gap-3 px-3 py-2.5 text-[11px] font-bold tracking-wide hover:bg-white/5 rounded-lg transition-colors cursor-pointer group">
                                            <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center text-white/40 group-hover:bg-pantheon-teal group-hover:text-white transition-colors">
                                                <User className="w-4 h-4" />
                                            </div>
                                            <span>Profile Settings</span>
                                        </Link>
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator className="bg-white/5 mx-1" />
                                    <DropdownMenuItem
                                        onClick={() => { logout(); navigate('/'); }}
                                        className="w-full flex items-center gap-3 px-3 py-2.5 text-[11px] font-bold tracking-wide hover:bg-red-500/10 text-red-400 hover:text-red-400 rounded-lg transition-colors cursor-pointer group border-none"
                                    >
                                        <div className="w-8 h-8 rounded-lg bg-red-500/10 flex items-center justify-center text-red-500 group-hover:bg-red-500 group-hover:text-white transition-colors">
                                            <LogOut className="w-4 h-4" />
                                        </div>
                                        <span>Logout</span>
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>
                    ) : (
                        <div className="flex items-center gap-3">
                            <ThemeToggle className="text-white" />
                            <Link
                                to="/auth?mode=signup"
                                className="px-6 py-2.5 border border-white/30 text-white hover:bg-white/10 rounded-md text-[11px] font-bold tracking-[0.2em] transition-all uppercase"
                            >
                                SIGN UP
                            </Link>
                            <Link to="/auth?mode=signin" className="px-6 py-2.5 bg-pantheon-teal text-white border border-pantheon-teal/40 hover:bg-pantheon-teal-dark rounded-md text-[11px] font-bold tracking-[0.2em] transition-all uppercase shadow-lg shadow-pantheon-teal/20">
                                SIGN IN
                            </Link>
                        </div>
                    )}
                </div>

                {/* Mobile-only top-right menu (desktop nav above is unchanged) */}
                <MobileMenu />
            </div>

        </motion.nav>
    );
}
